import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Sparkles, X } from "lucide-react";
import { useGetMyProfile, getGetMyProfileQueryKey } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import { cn } from "@/lib/utils";

const SHOW_ON_PATH_PREFIXES = [
  "/ontdekken",
  "/matches",
  "/berichten",
  "/profiel",
];

const HIDE_ON_PATHS = new Set(["/profiel/aanmaken"]);

const DISMISS_STORAGE_KEY = "kw_reminder_banner_dismissed_until";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;

function readDismissedUntil(): number {
  try {
    if (typeof window === "undefined") return 0;
    const raw = window.localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) return 0;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export function KinderwensReminderBanner() {
  const { isAuthenticated, user } = useAuth();
  const [location] = useLocation();
  const [dismissedUntil, setDismissedUntil] = useState<number>(0);

  useEffect(() => {
    setDismissedUntil(readDismissedUntil());
  }, []);

  const isAdmin = !!user && (user.role === "admin" || user.isAdmin === true);
  const isVerified = !!user?.isVerified;
  const onRelevantPath =
    SHOW_ON_PATH_PREFIXES.some((p) => location.startsWith(p)) &&
    !HIDE_ON_PATHS.has(location) &&
    !location.startsWith("/kinderwens-score");
  const isDismissed = dismissedUntil > Date.now();

  const enabled = isAuthenticated && isVerified && !isAdmin && onRelevantPath && !isDismissed;

  const { data: profile } = useGetMyProfile({
    query: {
      enabled,
      staleTime: 60_000,
      queryKey: getGetMyProfileQueryKey(),
    },
  });

  if (!enabled) return null;
  if (!profile) return null;
  if (profile.kinderwensComplete !== false) return null;

  function handleDismiss(): void {
    const until = Date.now() + DISMISS_DURATION_MS;
    try {
      if (typeof window !== "undefined") {
        window.localStorage.setItem(DISMISS_STORAGE_KEY, String(until));
      }
    } catch {
      /* localStorage onbeschikbaar; alleen in-memory verbergen */
    }
    setDismissedUntil(until);
  }

  return (
    <div
      data-testid="banner-kinderwens-reminder"
      className={cn(
        "w-full bg-gradient-to-r from-primary/10 via-pink-100/60 to-primary/10",
        "border-b border-primary/20",
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
        <div className="hidden sm:flex items-center justify-center w-9 h-9 rounded-full bg-primary/15 text-primary shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 text-sm text-foreground">
          <span className="font-semibold">Vul je KinderwensScore nog in.</span>{" "}
          <span className="text-muted-foreground">
            Zonder de vragenlijst kun je geen profielen ontdekken en zien anderen jouw match-percentage niet.
          </span>
        </div>
        <Link
          href="/kinderwens-score"
          data-testid="link-kinderwens-reminder"
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover transition-colors shrink-0"
        >
          Vragenlijst invullen
        </Link>
        <button
          type="button"
          aria-label="Sluit herinnering"
          data-testid="button-kinderwens-reminder-dismiss"
          onClick={handleDismiss}
          className="ml-1 inline-flex items-center justify-center w-8 h-8 rounded-md text-muted-foreground hover:bg-primary/10 hover:text-foreground transition-colors shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
